import React from 'react'
import { Portal } from 'react-portal'
import actions from 'redux/actions'
import cx from 'classnames'
import { constants, sentry } from 'common/helpers'
import { FormattedMessage } from 'sb-react-intl'
import { useState, useCallback, useBodyOverlay } from 'sb-hooks'
import useGlobals from 'sb-hooks/useGlobals'
import { useConnect } from 'sb-redux-hook'

import Overlay from 'components/layout/Overlay/Overlay'
import CloseIcon from 'components/ui/CloseIcon/CloseIcon'
import useCanUsePrepaidPlans from 'components/upgradePlans/useCanUsePrepaidPlans'

import getNav from './util/getNav'
import SidebarNavItems from './SidebarNavItems/SidebarNavItems'

import messages from './messages'
import s from './SidebarNav.scss'


const SidebarNav = () => {
  const { state, isOpened } = useConnect({
    state: (state) => state,
    isOpened: 'models.ui.isSideMenuVisible',
  })

  useBodyOverlay(isOpened)

  const globals = useGlobals(({ getFeatureToggle, getAbValue }) => ({
    isCollectionsEnabled: getFeatureToggle(constants.features.productsCollections),
    resubscribeBurgerMenuAbValue: getAbValue(constants.abTests.resubscribeBurgerMenu),
    isIncubatorNeonHeatEnabled: getFeatureToggle(constants.features.incubatorNeonHeat),
    isResubscribeMembershipPageEnabled: getFeatureToggle(constants.features.resubscribeMembershipPage),
    isFatherDayBambooGiftSetEnabled: getFeatureToggle(constants.features.fatherDayBambooGiftSet),
    isPopularInStatesHidden: getFeatureToggle(constants.features.hidePopularInStates),
    isFragranceFinderHidden: getFeatureToggle(constants.features.hideFragranceFinder),
    isFragranceHoroscopeHidden: getFeatureToggle(constants.features.hideFragranceHoroscope),
    isGiftsHidden: getFeatureToggle(constants.features.hideGifts),
    isSeparateWellnessEnabled: getFeatureToggle(constants.features.separateWellness),
  }))

  globals.isPrepaidPlansLandingPageEnabled = useCanUsePrepaidPlans()

  const [ isBlock, setBlock ]               = useState(false)
  const [ activeIndexes, setActiveIndexes ] = useState([])

  const nav = getNav({ state, globals })

  const next = useCallback((index) => {
    if (!isBlock) {
      setBlock(true)
      setActiveIndexes([ ...activeIndexes, index ])

      // blocks double clicking on an item during an animation
      setTimeout(() => setBlock(false), 600)
    }
  }, [ activeIndexes, isBlock ])

  const back = useCallback(() => {
    setActiveIndexes(activeIndexes.slice(0, activeIndexes.length - 1))
  }, [ activeIndexes ])

  const close = useCallback(() => {
    setActiveIndexes([])
    actions.models.ui.closeSideMenu()
  }, [])

  const renderItems = useCallback((items = [], index) => {

    if (!Array.isArray(items)) {
      return close()
    }

    return (
      <div className={s.section} key={index}>
        <div className={s.header}>
          {
            Boolean(index) ? (
              <div className={s.backLink} onClick={back} data-testid="backLink">
                <div className={s.backIcon} />
                <FormattedMessage tag="div" className={s.headerTitle} {...messages.back} />
              </div>
            ) : (
              <CloseIcon className={s.closeIcon} whiteColor onClick={close} />
            )
          }
        </div>
        <SidebarNavItems
          items={items}
          closeSidebar={close}
          changeActiveIndex={next}
        />
      </div>
    )
  }, [ back, close, next ])

  const getActiveNav = useCallback((activeIndexes) => (
    Array.isArray(activeIndexes)
      ? activeIndexes.reduce((acc, itemIndex) => acc[itemIndex].subNav, nav)
      : []
  ), [ nav ])

  const renderContent = useCallback(() => {
    const count  = Array.isArray(activeIndexes) && activeIndexes.length
    let content  = [ nav ]

    if (count) {
      try {
        content = [ ...content, ...activeIndexes.reduce((acc, _, index) => (
          [ ...acc, getActiveNav(index === count - 1 ? activeIndexes : [ activeIndexes[0] ]) ]
        ), []) ]
      }
      catch (error) {
        setActiveIndexes([])
      }
    }

    return content.map(renderItems)
  }, [ activeIndexes, nav, getActiveNav, renderItems ])

  const sideMenuClassName = cx(s.sideMenu, {
    [s.opened]: isOpened,
  })

  const style = {}

  if (activeIndexes.length) {
    style.transform = `translate3d(-${activeIndexes.length}00%, 0, 0)`
  }

  return (
    <Portal>
      <div>
        {
          isOpened && (
            <Overlay className={s.overlay} onClick={close} />
          )
        }
        <div className={sideMenuClassName}>
          <div className={s.content} style={style}>
            {renderContent()}
          </div>
        </div>
      </div>
    </Portal>
  )
}


export default SidebarNav
